import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, CalendarDays, Clock, RefreshCw, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, fmtMoney, apiError, fmtTimeStr } from "@/lib/api";

export default function MyBookings() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/lookup?email=${encodeURIComponent(email.trim())}`);
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const requestChange = async (b, type) => {
    const message = window.prompt(type === "cancel" ? "Reason for cancellation (optional):" : "Preferred new date/time:");
    if (message === null) return;
    try {
      await api.post(`/bookings/${b.id}/request-change`, { type, message });
      toast.success("Request sent — the studio will be in touch.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="pt-28 pb-24 max-w-3xl mx-auto px-5" data-testid="my-bookings-page">
      <p className="font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37]">Lookup</p>
      <h1 className="font-display text-5xl md:text-6xl text-white mt-3 mb-6">My Bookings</h1>
      <p className="text-white/60 mb-6">Enter the email you booked with to view your paid sessions.</p>

      <div className="flex gap-2 mb-10">
        <Input
          data-testid="lookup-email-input"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          className="bg-[#111] border-white/15"
        />
        <button onClick={search} data-testid="lookup-btn" className="btn-gold shrink-0 rounded-md px-6 flex items-center gap-2 text-sm font-mono-label uppercase">
          <Search size={16} /> Find
        </button>
      </div>

      {loading && <p className="text-white/40">Searching…</p>}

      {result && (
        <div className="space-y-10">
          <Section title="Upcoming" bookings={result.upcoming} onChange={requestChange} empty="No upcoming sessions." />
          <Section title="Past" bookings={result.past} onChange={null} empty="No past sessions." />
        </div>
      )}
    </div>
  );
}

function Section({ title, bookings, onChange, empty }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-white mb-4">{title}</h2>
      {bookings.length === 0 ? (
        <p className="text-white/40 text-sm">{empty}</p>
      ) : (
        <div className="space-y-3">
          {bookings.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#111] border border-white/10 rounded-2xl p-5"
              data-testid={`booking-card-${i}`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-display text-lg text-white">{b.service_title}</h3>
                  <p className="text-white/50 text-sm flex items-center gap-2 mt-1"><CalendarDays size={14} className="text-[#D4AF37]" /> {b.event_date}</p>
                  <p className="text-white/50 text-sm flex items-center gap-2 mt-0.5"><Clock size={14} className="text-[#D4AF37]" /> {fmtTimeStr(b.event_time)} · {b.hours}h</p>
                </div>
                <span className="text-[#D4AF37] font-display text-lg">{fmtMoney(b.total_cents)}</span>
              </div>
              {b.change_request && !b.change_request.resolved && (
                <p className="text-amber-400/80 text-xs mt-3">Change requested ({b.change_request.type}) — pending studio review.</p>
              )}
              {onChange && (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => onChange(b, "reschedule")} data-testid={`reschedule-${i}`} className="text-xs flex items-center gap-1.5 border border-white/15 rounded-full px-4 py-2 text-white/70 hover:border-[#D4AF37]">
                    <RefreshCw size={13} /> Reschedule
                  </button>
                  <button onClick={() => onChange(b, "cancel")} data-testid={`cancel-${i}`} className="text-xs flex items-center gap-1.5 border border-white/15 rounded-full px-4 py-2 text-white/70 hover:border-red-500/60 hover:text-red-400">
                    <X size={13} /> Request Cancel
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
